import { Button } from '@Components/button/button'
import { FormFloatingFooter } from '../claim-flow/form-floating-footer'
import {
  ClaimReservationCommunicationMethod,
  ClaimReservationResponse,
} from '@interaction-gaming/player-data-service-browser-client'
import { ComponentUtils } from '@Components/utils'

import DetailsBackground from '@Assets/confirmation-background.png'

import './status-screen.scss'
import { RootState } from '@AppState/redux-store'
import { useDispatch, useSelector } from 'react-redux'
import { hostConfigByName } from '../../config/utils'
import { cloneElement } from 'react'
import { CalendarEventCreate } from '@Components/calendar-event-creator/calendar-event-creator'
import { DateTime } from 'luxon'
import { SUPPORTED_CALENDARS } from '@Components/calendar-event-creator/types'
import { ImageBackgroundSection } from '@Components/image-background-section/image-background-section'
import { Header } from '@Components/header/header'
import { useLocation, useNavigate } from 'react-router-dom'
import { ClaimReservationActions } from '@AppState/claim-reservation'
import { ContainerMessenger } from '../../container-messenger/container-messenger'
import { ContainerMessageType } from '../../container-messenger/types'
import { Utils } from '../../utilities/utils'
import { QUERY_PARAMS, FLOWS } from '../../constants'

const CLASS_NAME = 'status-screen'

type ConfirmationScreenProps = {
  reservation: ClaimReservationResponse
}

export const ConfirmationScreen = ({ reservation }: ConfirmationScreenProps) => {
  const hostName = useSelector((state: RootState) => state.appConfig.config?.hostName)
  const enableClaimForm = useSelector((state: RootState) => state.appConfig.config?.enableClaimForm)

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  if (!hostName) {
    throw new Error('Unable to determine host name')
  }
  const hostConfig = hostConfigByName[hostName]

  const dateInfo = reservation.timeslot.display.dateComponents

  const showClaimFormCta = !!enableClaimForm && !!hostConfig.confirmationScreen.claimFormSections

  const handleBeginClaimForm = () => {
    const params = new URLSearchParams(location.search)
    params.set(QUERY_PARAMS.CLAIM_ID, reservation.id)
    params.set(QUERY_PARAMS.FLOW, FLOWS.CLAIM_FORM)
    navigate(`/?${params.toString()}`)
  }

  const handleComplete = () => {
    ContainerMessenger.sendMessage({ type: ContainerMessageType.EXIT, payload: {} })
    dispatch(ClaimReservationActions.clearSavedReservation())
    navigate(`/create${location.search}`)
  }

  return (
    <>
      <Header title="Appointment Confirmation" />

      <section>
        <ImageBackgroundSection backgroundUrl={DetailsBackground}>
          <section className={`${CLASS_NAME}__details-text`}>
            <p>Confirmed for</p>
            <h1>{`${dateInfo.dayOfWeek}`}</h1>
            <h1>
              {`${dateInfo.month} ${dateInfo.dayOfMonth}${ComponentUtils.suffixForDateNumber(Number(dateInfo.dayOfMonth))} @ ${Utils.formatLocalTime(reservation.timeslot.display.localTime)}`}
              ,
            </h1>
            <p>at</p>
            <h3>{reservation.claimCenter.name}</h3>

            <section className={`${CLASS_NAME}__details-address`}>
              <p>{reservation.claimCenter.buildingName}</p>
              <p>{reservation.claimCenter.address1}</p>
              <p>{reservation.claimCenter.address2}</p>
              <p>
                {reservation.claimCenter.city}, {reservation.claimCenter.region}{' '}
                {reservation.claimCenter.postalCode}
              </p>
            </section>
          </section>
          <section className={`${CLASS_NAME}__details-text`}>
            <h3>Add to Calendar</h3>
            <CalendarEventCreate
              calendarTypes={Object.values(SUPPORTED_CALENDARS)}
              title={hostConfig.calendarEvent.title}
              details={hostConfig.calendarEvent.details}
              location={`${reservation.claimCenter.address1} ${reservation.claimCenter.address2} ${reservation.claimCenter.city}, ${reservation.claimCenter.region}`}
              startTime={DateTime.fromFormat(
                `${reservation.timeslot.display.localDate} ${reservation.timeslot.display.isoStartTime}`,
                'yyyy-MM-dd HH:mm:ss',
              )}
              endTime={DateTime.fromFormat(
                `${reservation.timeslot.display.localDate} ${reservation.timeslot.display.isoEndTime}`,
                'yyyy-MM-dd HH:mm:ss',
              )}
            />
          </section>
        </ImageBackgroundSection>
        <section className={`${CLASS_NAME}__general-body`}>
          <p>
            We’ve sent a{' '}
            {reservation.claimReservationDetails.communicationPreference ===
            ClaimReservationCommunicationMethod.PHONE
              ? 'text'
              : 'email'}{' '}
            to the{' '}
            {reservation.claimReservationDetails.communicationPreference ===
            ClaimReservationCommunicationMethod.PHONE
              ? 'number'
              : 'email'}{' '}
            you provided confirming the date, time, and location. Remember to bring the following to
            your appointment:
          </p>
          <ul>
            {hostConfig.confirmationScreen.itemsNeeded.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          {hostConfig.confirmationScreen.disclaimer}

          {showClaimFormCta
            ? hostConfig.confirmationScreen.claimFormSections!(handleBeginClaimForm).map(
                (element, index) => cloneElement(element, { key: index }),
              )
            : hostConfig.confirmationScreen.additionalSections.map((element, index) =>
                cloneElement(element, { key: index }),
              )}
        </section>

        <FormFloatingFooter>
          <section className={`floating-footer__buttons`}>
            {showClaimFormCta ? (
              <Button
                className={`floating-footer__next-button`}
                disabled={false}
                onClick={handleBeginClaimForm}>
                Begin Claim Form
              </Button>
            ) : (
              <Button
                className={`floating-footer__next-button`}
                disabled={false}
                onClick={handleComplete}>
                Complete
              </Button>
            )}
          </section>
        </FormFloatingFooter>
      </section>
    </>
  )
}
